import { unsafeCSS, html } from "lit";
import { state, property } from "lit/decorators.js";
import { LeafElement } from "../../components/leaf-element.js";
import { appendStylesheet } from "../../utilities/light-dom-styles.js";
const styles = `@charset "UTF-8";
/*------------------------------------*\\
    #RESET
\\*------------------------------------*/
/**
 * Border-Box http:/paulirish.com/2012/box-sizing-border-box-ftw/
 */
*,
::slotted(*),
*:before,
*:after {
  box-sizing: border-box;
}

:host *,
:host *::before,
:host *::after {
  box-sizing: inherit;
}

[hidden] {
  display: none !important;
}

*,
::before,
::after {
  box-sizing: border-box;
}

:where(html) {
  block-size: 100%;
  font-family: var(--leaf-typography-body-default-font-family);
  font-size: var(--leaf-typography-body-default-font-size);
  font-weight: var(--leaf-typography-body-default-font-weight);
  line-height: var(--leaf-typography-body-default-line-height);
  color: var(--leaf-color-content-default);
  background-color: var(--background-default);
  scrollbar-gutter: stable;
  -webkit-text-size-adjust: none;
     -moz-text-size-adjust: none;
          text-size-adjust: none;
}

:where(:not(:active):focus-visible) {
  outline-offset: 1px;
}

:where(body) {
  /* Improve text rendering */
  -webkit-font-smoothing: antialiased;
  /* https://marco.org/2012/11/15/text-rendering-optimize-legibility */
  text-rendering: optimizeSpeed;
  margin: 0;
  /* Allow percentage-based heights in the application */
  min-block-size: 100%;
  overflow-x: hidden;
}
:where(body).backdrop-active {
  overflow-y: hidden;
}

:where(p, h1, h2, h3, h4, h5, h6) {
  overflow-wrap: break-word;
  margin-block: var(--leaf-spacing-16) 0;
  margin-inline: 0;
  word-break: break-word;
  scroll-margin-top: var(--leaf-spacing-32);
  /* Target WebKit (Safari) browsers */
}
:where(p, h1, h2, h3, h4, h5, h6):not(li p):first-child {
  margin-block-start: 0;
}
:where(p, h1, h2, h3, h4, h5, h6):not(li p):last-child {
  margin-block-end: 0;
}
@supports (-webkit-touch-callout: none) {
  @media (max-width: 47.999rem) {
    :where(p, h1, h2, h3, h4, h5, h6) {
      scroll-margin-top: calc(var(--leaf-spacing-32) * 2);
    }
  }
}

:where(h1, h2, h3, h4, h5, h6):not([slot]) {
  color: var(--leaf-color-content-brand-strong);
}

:where(h1) {
  font-family: var(--leaf-typography-display-default-mobile-font-family);
  font-size: var(--leaf-typography-display-default-mobile-font-size);
  font-weight: var(--leaf-typography-display-default-mobile-font-weight);
  line-height: var(--leaf-typography-display-default-mobile-line-height);
  -webkit-text-decoration: var(--leaf-typography-display-default-mobile-text-decoration);
          text-decoration: var(--leaf-typography-display-default-mobile-text-decoration);
  text-transform: var(--leaf-typography-display-default-mobile-text-transform);
}
@media (min-width: 48rem) {
  :where(h1) {
    font-family: var(--leaf-typography-display-default-font-family);
    font-size: var(--leaf-typography-display-default-font-size);
    font-weight: var(--leaf-typography-display-default-font-weight);
    line-height: var(--leaf-typography-display-default-line-height);
    -webkit-text-decoration: var(--leaf-typography-display-default-text-decoration);
            text-decoration: var(--leaf-typography-display-default-text-decoration);
    text-transform: var(--leaf-typography-display-default-text-transform);
  }
}

:where(h2) {
  font-family: var(--leaf-typography-headline-large-mobile-font-family);
  font-size: var(--leaf-typography-headline-large-mobile-font-size);
  font-weight: var(--leaf-typography-headline-large-mobile-font-weight);
  line-height: var(--leaf-typography-headline-large-mobile-line-height);
  -webkit-text-decoration: var(--leaf-typography-headline-large-mobile-text-decoration);
          text-decoration: var(--leaf-typography-headline-large-mobile-text-decoration);
  text-transform: var(--leaf-typography-headline-large-mobile-text-transform);
}
@media (min-width: 48rem) {
  :where(h2) {
    font-family: var(--leaf-typography-headline-large-font-family);
    font-size: var(--leaf-typography-headline-large-font-size);
    font-weight: var(--leaf-typography-headline-large-font-weight);
    line-height: var(--leaf-typography-headline-large-line-height);
    -webkit-text-decoration: var(--leaf-typography-headline-large-text-decoration);
            text-decoration: var(--leaf-typography-headline-large-text-decoration);
    text-transform: var(--leaf-typography-headline-large-text-transform);
  }
}

:where(h3) {
  font-family: var(--leaf-typography-headline-default-mobile-font-family);
  font-size: var(--leaf-typography-headline-default-mobile-font-size);
  font-weight: var(--leaf-typography-headline-default-mobile-font-weight);
  line-height: var(--leaf-typography-headline-default-mobile-line-height);
  -webkit-text-decoration: var(--leaf-typography-headline-default-mobile-text-decoration);
          text-decoration: var(--leaf-typography-headline-default-mobile-text-decoration);
  text-transform: var(--leaf-typography-headline-default-mobile-text-transform);
}
@media (min-width: 48rem) {
  :where(h3) {
    font-family: var(--leaf-typography-headline-default-font-family);
    font-size: var(--leaf-typography-headline-default-font-size);
    font-weight: var(--leaf-typography-headline-default-font-weight);
    line-height: var(--leaf-typography-headline-default-line-height);
    -webkit-text-decoration: var(--leaf-typography-headline-default-text-decoration);
            text-decoration: var(--leaf-typography-headline-default-text-decoration);
    text-transform: var(--leaf-typography-headline-default-text-transform);
  }
}

:where(h4) {
  font-family: var(--leaf-typography-headline-small-mobile-font-family);
  font-size: var(--leaf-typography-headline-small-mobile-font-size);
  font-weight: var(--leaf-typography-headline-small-mobile-font-weight);
  line-height: var(--leaf-typography-headline-small-mobile-line-height);
  -webkit-text-decoration: var(--leaf-typography-headline-small-mobile-text-decoration);
          text-decoration: var(--leaf-typography-headline-small-mobile-text-decoration);
  text-transform: var(--leaf-typography-headline-small-mobile-text-transform);
}
@media (min-width: 48rem) {
  :where(h4) {
    font-family: var(--leaf-typography-headline-small-font-family);
    font-size: var(--leaf-typography-headline-small-font-size);
    font-weight: var(--leaf-typography-headline-small-font-weight);
    line-height: var(--leaf-typography-headline-small-line-height);
    -webkit-text-decoration: var(--leaf-typography-headline-small-text-decoration);
            text-decoration: var(--leaf-typography-headline-small-text-decoration);
    text-transform: var(--leaf-typography-headline-small-text-transform);
  }
}

:where(h5) {
  font-family: var(--leaf-typography-title-large-mobile-font-family);
  font-size: var(--leaf-typography-title-large-mobile-font-size);
  font-weight: var(--leaf-typography-title-large-mobile-font-weight);
  line-height: var(--leaf-typography-title-large-mobile-line-height);
  -webkit-text-decoration: var(--leaf-typography-title-large-mobile-text-decoration);
          text-decoration: var(--leaf-typography-title-large-mobile-text-decoration);
  text-transform: var(--leaf-typography-title-large-mobile-text-transform);
}
@media (min-width: 48rem) {
  :where(h5) {
    font-family: var(--leaf-typography-title-large-font-family);
    font-size: var(--leaf-typography-title-large-font-size);
    font-weight: var(--leaf-typography-title-large-font-weight);
    line-height: var(--leaf-typography-title-large-line-height);
    -webkit-text-decoration: var(--leaf-typography-title-large-text-decoration);
            text-decoration: var(--leaf-typography-title-large-text-decoration);
    text-transform: var(--leaf-typography-title-large-text-transform);
  }
}

:where(h6) {
  font-family: var(--leaf-typography-title-default-font-family);
  font-size: var(--leaf-typography-title-default-font-size);
  font-weight: var(--leaf-typography-title-default-font-weight);
  line-height: var(--leaf-typography-title-default-line-height);
  -webkit-text-decoration: var(--leaf-typography-title-default-text-decoration);
          text-decoration: var(--leaf-typography-title-default-text-decoration);
  text-transform: var(--leaf-typography-title-default-text-transform);
}

:where(small) {
  font-family: var(--leaf-typography-body-small-font-family);
  font-size: var(--leaf-typography-body-small-font-size);
  font-weight: var(--leaf-typography-body-small-font-weight);
  line-height: var(--leaf-typography-body-small-line-height);
  -webkit-text-decoration: var(--leaf-typography-body-small-text-decoration);
          text-decoration: var(--leaf-typography-body-small-text-decoration);
  text-transform: var(--leaf-typography-body-small-text-transform);
}

:where(u, ins, abbr) {
  text-underline-offset: 3px;
  text-decoration-thickness: var(--leaf-border-width-default);
}
@supports (-moz-appearance: none) {
  :where(u, ins, abbr) {
    text-underline-offset: 3px;
  }
}

:where(a[href], area, button, input, label[for], select, summary, textarea, [tabindex]:not([tabindex*="-"])) {
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

:where(form) leaf-input:not([type=hidden]),
:where(form) leaf-select:not([type=hidden]),
:where(form) leaf-textarea:not([type=hidden]),
:where(form) leaf-radiogroup:not([type=hidden]),
:where(form) leaf-checkgroup:not([type=hidden]),
:where(form) leaf-inputgroup:not([type=hidden]),
:where(form) leaf-button:not([type=hidden]),
:where(form) > leaf-label:not([type=hidden]) {
  margin-top: var(--leaf-spacing-24);
  max-width: 100%;
}
:where(form) leaf-button {
  margin-top: var(--leaf-spacing-16);
}
:where(form) leaf-label + leaf-input:not([type=hidden]),
:where(form) leaf-label + leaf-select:not([type=hidden]),
:where(form) leaf-label + leaf-textarea:not([type=hidden]) {
  margin-top: var(--leaf-spacing-4);
}
:where(form) leaf-label + leaf-radiogroup:not([type=hidden]),
:where(form) leaf-label + leaf-checkgroup:not([type=hidden]),
:where(form) leaf-label + leaf-inputgroup:not([type=hidden]) {
  margin-top: var(--leaf-spacing-8);
}

:where([href^=tel]) {
  white-space: nowrap;
}
@media (min-width: 48.001rem) {
  :where([href^=tel]) {
    border-bottom: 0 !important;
    color: inherit !important;
    cursor: default !important;
    font-weight: inherit !important;
    padding-bottom: 0 !important;
    pointer-events: none;
  }
}

:where(a) {
  font-family: var(--leaf-typography-body-default-link-font-family);
  font-size: inherit;
  font-weight: var(--leaf-typography-body-default-link-font-weight);
  line-height: inherit;
  -webkit-text-decoration: var(--leaf-text-decoration-none);
          text-decoration: var(--leaf-text-decoration-none);
  text-transform: var(--leaf-typography-body-default-link-text-transform);
  border-bottom: var(--leaf-border-width-default) solid;
  color: var(--leaf-color-content-link);
  word-break: break-word;
}
:where(a):hover {
  color: var(--leaf-color-content-link-hover);
}
:where(a):focus {
  background-color: var(--leaf-color-bg-active);
  border-color: var(--leaf-color-border-transparent);
  color: var(--leaf-color-content-knockout);
  outline: none;
}
:where(a):where(.leaf-u-link--external)::after {
  background-color: currentColor;
  content: ".";
  margin: 0 0 0 var(--leaf-spacing-4);
  padding: 0 calc(var(--leaf-spacing-8) - var(--leaf-spacing-2));
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' id='svg_1' fill='%23333333' d='m28.667,28.667l-9.334,0l0,-9.334l4.667,0l0,-1.333l-4.667,0c-0.74,0 -1.333,0.6 -1.333,1.333l0,9.334c0,0.733 0.593,1.333 1.333,1.333l9.334,0c0.733,0 1.333,-0.6 1.333,-1.333l0,-4.667l-1.333,0l0,4.667zm-3.334,-10.667l0,1.333l2.394,0l-6.554,6.554l0.94,0.94l6.554,-6.554l0,2.394l1.333,0l0,-4.667l-4.667,0z'/%3E%3C/g%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' id='svg_1' fill='%23333333' d='m28.667,28.667l-9.334,0l0,-9.334l4.667,0l0,-1.333l-4.667,0c-0.74,0 -1.333,0.6 -1.333,1.333l0,9.334c0,0.733 0.593,1.333 1.333,1.333l9.334,0c0.733,0 1.333,-0.6 1.333,-1.333l0,-4.667l-1.333,0l0,4.667zm-3.334,-10.667l0,1.333l2.394,0l-6.554,6.554l0.94,0.94l6.554,-6.554l0,2.394l1.333,0l0,-4.667l-4.667,0z'/%3E%3C/g%3E%3C/svg%3E");
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
}
:where(a):where(.leaf-u-link--lock)::after {
  background-color: currentColor;
  content: ".";
  margin: 0 0 0 var(--leaf-spacing-8);
  padding: 0 calc(var(--leaf-spacing-8) - var(--leaf-spacing-2));
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' d='m28.5,21.25l-0.75,0l0,-1.571c0,-2.169 -1.68,-3.929 -3.75,-3.929c-2.07,0 -3.75,1.76 -3.75,3.929l0,1.571l-0.75,0c-0.825,0 -1.5,0.707 -1.5,1.571l0,7.858c0,0.864 0.675,1.571 1.5,1.571l9,0c0.825,0 1.5,-0.707 1.5,-1.571l0,-7.859c0,-0.864 -0.675,-1.571 -1.5,-1.571l0,0.001zm-6.75,-1.571c0,-1.305 1.005,-2.358 2.25,-2.358s2.25,1.053 2.25,2.358l0,1.571l-4.5,0l0,-1.571zm6.75,11l-9,0l0,-7.859l9,0l0,7.858l0,0.001zm-4.5,-2.359c0.825,0 1.5,-0.707 1.5,-1.571s-0.675,-1.571 -1.5,-1.571s-1.5,0.707 -1.5,1.571s0.675,1.571 1.5,1.571z' fill='%23333333' id='svg_1'/%3E%3C/g%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' d='m28.5,21.25l-0.75,0l0,-1.571c0,-2.169 -1.68,-3.929 -3.75,-3.929c-2.07,0 -3.75,1.76 -3.75,3.929l0,1.571l-0.75,0c-0.825,0 -1.5,0.707 -1.5,1.571l0,7.858c0,0.864 0.675,1.571 1.5,1.571l9,0c0.825,0 1.5,-0.707 1.5,-1.571l0,-7.859c0,-0.864 -0.675,-1.571 -1.5,-1.571l0,0.001zm-6.75,-1.571c0,-1.305 1.005,-2.358 2.25,-2.358s2.25,1.053 2.25,2.358l0,1.571l-4.5,0l0,-1.571zm6.75,11l-9,0l0,-7.859l9,0l0,7.858l0,0.001zm-4.5,-2.359c0.825,0 1.5,-0.707 1.5,-1.571s-0.675,-1.571 -1.5,-1.571s-1.5,0.707 -1.5,1.571s0.675,1.571 1.5,1.571z' fill='%23333333' id='svg_1'/%3E%3C/g%3E%3C/svg%3E");
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: var(--leaf-spacing-40);
          mask-size: var(--leaf-spacing-40);
}
:where(a):where(.leaf-u-link--inverted) {
  color: var(--leaf-color-content-knockout);
}
:where(a):where(.leaf-u-link--inverted):hover {
  color: var(--leaf-color-content-brand-knockout);
}
:where(a):where(.leaf-u-link--inverted):focus {
  background-color: var(--leaf-color-content-knockout);
  color: var(--leaf-color-bg-active);
}
:where(a):where(.leaf-u-link--action) {
  border-bottom: none;
  display: inline-block;
  line-height: var(--leaf-spacing-32);
  margin: var(--leaf-spacing-8) calc(var(--leaf-spacing-48) - var(--leaf-spacing-4));
  margin-left: 0;
  position: relative;
}
:where(a):where(.leaf-u-link--action)::before {
  content: "";
  display: inline-block;
  height: 100%;
  min-height: var(--leaf-spacing-40);
  vertical-align: middle;
}
:where(a):where(.leaf-u-link--action)::after {
  background-color: currentColor;
  bottom: 0;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='white'/%3E%3C/svg%3E");
  height: var(--leaf-spacing-40);
  margin-left: var(--leaf-spacing-4);
  position: absolute;
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23001F99'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='white'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23001F99' stroke-width='2'/%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23001F99'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='white'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23001F99' stroke-width='2'/%3E%3C/svg%3E");
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  width: var(--leaf-spacing-40);
}
:where(a):where(.leaf-u-link--action):focus:after {
  border-radius: 50%;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5.28027' y='5' width='30' height='30' rx='15' fill='%23001F99'/%3E%3Cpath d='M20.2803 12L18.8703 13.41L24.4503 19H12.2803V21H24.4503L18.8703 26.59L20.2803 28L28.2803 20L20.2803 12Z' fill='white'/%3E%3Crect x='39.2803' y='39' width='38' height='38' rx='19' transform='rotate(180 39.2803 39)' stroke='black' stroke-width='2'/%3E%3Crect x='5.28027' y='5' width='30' height='30' rx='15' stroke='%23001F99' stroke-width='2'/%3E%3C/svg%3E");
  -webkit-mask: none;
          mask: none;
}
:where(a):where(.leaf-u-link--action):where(.leaf-u-link--inverted)::after {
  border-radius: 50%;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3C/svg%3E");
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='white'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='white' stroke-width='2'/%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='white'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='white' stroke-width='2'/%3E%3C/svg%3E");
}
:where(a):where(.leaf-u-link--action):where(.leaf-u-link--inverted):hover::after {
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23E6EBFF'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23E6EBFF' stroke-width='2'/%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23E6EBFF'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23E6EBFF' stroke-width='2'/%3E%3C/svg%3E");
}
:where(a):where(.leaf-u-link--action):where(.leaf-u-link--inverted):focus:after {
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23E6EBFF'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='white' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23E6EBFF' stroke-width='2'/%3E%3C/svg%3E");
  -webkit-mask: none;
          mask: none;
}

:where(span) {
  /* is this needed ? */
  /* https://stackoverflow.com/questions/1556363/weird-span-element-rendering-in-safari-on-iphone */
  -webkit-text-size-adjust: none;
     -moz-text-size-adjust: none;
          text-size-adjust: none;
}

:where(img, svg, video, canvas, audio, iframe, embed, object) {
  display: block;
}

:where(img, svg, video) {
  max-inline-size: 100%;
  block-size: auto;
}

:where(strong, b, dt, summary) {
  font-weight: var(--leafdcws-font-weight-bold);
}

:where(p, address, ol, ul, dl, dt:not(:first-of-type)) {
  margin-block-start: var(--leaf-spacing-16);
  margin-block-end: 0;
}

:where(address) {
  font-style: normal;
}

:where(ol, ul) {
  padding-inline-start: var(--leaf-spacing-16);
}

:where(ol ol, ul ul, ol ul, ul ol) {
  margin-block: 0;
}

:where(ul > li > ul > li) {
  list-style-type: circle;
}
:where(ul > li > ul > li) ul > li {
  list-style-type: square;
}
:where(ul > li > ul > li) ul > li ul > li {
  list-style-type: disc;
}

:where(ul) {
  list-style-type: disc;
}

:where(ol) {
  list-style-type: decimal;
}

:where(ol > li > ol) {
  list-style-type: lower-alpha;
}

:where(ol > li > ol > li > ol) {
  list-style-type: lower-roman;
}

:where(li) {
  padding-inline-start: 0;
}

.leaf-c-content-block ul,
.leaf-c-content-block ol {
  display: flex;
  flex-direction: column;
  gap: var(--leaf-spacing-16);
  padding-inline-start: var(--leaf-spacing-32);
}

.visually-hidden {
  clip: rect(0 0 0 0);
  -webkit-clip-path: inset(50%);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

.no-focus-visible-outline:focus-visible {
  outline: none;
}

/* ------------------------------------*\\
    #TYPOGRAPHY PLACEHOLDERS
\\*------------------------------------ */
/*
  Hiding class, making content visible only to screen readers but not visually ("sr" meaning "screen-reader")
  Is the hidden content read by a screen reader?	- Yes
  Will the hidden element affect the document layout? - No
  Will the hidden element’s box model be rendered? - No
  Does the element detect clicks or focus? - Yes
  https://css-tricks.com/comparing-various-ways-to-hide-things-in-css/#method-5-the-visually-hidden-class
*/
:host table thead .leaf-u-visually-hidden, :host caption, :host([variant=column-stacker]) .mobileHeader .leaf-u-visually-hidden,
:host([variant=row-stacker]) .mobileHeader .leaf-u-visually-hidden,
:host([variant=graphical]) .mobileHeader .leaf-u-visually-hidden {
  clip: rect(0 0 0 0);
  -webkit-clip-path: inset(50%);
          clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  white-space: nowrap;
  width: 1px;
}

/* ------------------------------------*\\
    #BACKGROUND UTILITY CLASSES
\\*------------------------------------ */
.leaf-u-background-white {
  background: var(--leaf-color-bg-default);
}

.leaf-u-background-gray {
  background: var(--leaf-color-header-bg-tertiary);
}

.leaf-u-background-success {
  background-color: var(--leaf-color-bg-success);
}

.leaf-u-background-error {
  background-color: var(--leaf-color-bg-error);
}

.leaf-u-background-warning {
  background-color: var(--leaf-color-bg-warning);
}

.leaf-u-background-disabled {
  background-color: var(--leaf-color-bg-disabled);
}

/* ------------------------------------*\\
  Color utility classes
\\*------------------------------------ */
.leaf-u-color-content-brand-strong {
  color: var(--leaf-color-content-brand-strong);
}

.leaf-u-color-content-brand {
  color: var(--leaf-color-content-brand);
}

.leaf-u-color-content-brand-knockout {
  color: var(--leaf-color-content-brand-knockout);
}

.leaf-u-color-content-knockout {
  color: var(--leaf-color-content-knockout);
}

.leaf-u-color-content-subtle {
  color: var(--leaf-color-content-subtle);
}

.leaf-u-color-content-default {
  color: var(--leaf-color-content-default);
}

.leaf-u-color-bg-accent-1-xstrong {
  color: var(--leafdcws-accent-1-xstrong);
}

.leaf-u-color-bg-accent-2-xstrong {
  color: var(--leafdcws-accent-2-xstrong);
}

.leaf-u-color-bg-brand-strong {
  color: var(--leaf-color-bg-brand-strong);
}

.leaf-u-color-bg-brand-xstrong {
  color: var(--leaf-color-bg-brand-xstrong);
}

.leaf-u-color-bg-error-strong {
  color: var(--leaf-color-bg-error-strong);
}

:host([table-dark-border]) table thead tr th:first-child:not(:only-child), :host([variant=graphical]) table thead tr th:first-child:not(:only-child), :host([variant=scrollable]) table thead tr th:first-child:not(:only-child) {
  border-right-color: var(--table-dark-border-color) !important;
}
:host([table-dark-border]) table tbody tr th, :host([variant=graphical]) table tbody tr th, :host([variant=scrollable]) table tbody tr th,
:host([table-dark-border]) table tbody tr td:first-child:not(:only-child),
:host([variant=graphical]) table tbody tr td:first-child:not(:only-child),
:host([variant=scrollable]) table tbody tr td:first-child:not(:only-child) {
  border-right-color: var(--table-dark-border-color) !important;
}

:host {
  --table-border-color: var(--leaf-color-border-default);
  --table-dark-border-color: var(--leaf-color-border-strong);
  --table-border: var(--leaf-border-width-default) solid var(--table-border-color);
  --table-header-background-color: var(--leaf-color-bg-subtle);
  --table-spacing: var(--leaf-spacing-24);
  --table-row-min-height: var(--leaf-spacing-32);
  --table-fixed-column-elevation: 6px 0 10px -2px var(--leaf-color-border-default);
  --table-th-min-width: 140px;
  display: grid;
  width: 100%;
  word-break: keep-all;
}
:host table {
  border-collapse: collapse;
  border-color: transparent;
  border-spacing: 0;
  width: 100%;
}
@media (min-width: 35rem) {
  :host table th {
    padding: var(--leaf-spacing-24);
  }
  :host table td {
    padding: var(--leaf-spacing-24);
  }
}
:host table tbody,
:host table tbody p,
:host table tbody ul li {
  font-family: var(--leaf-typography-body-default-font-family);
  font-size: var(--leaf-typography-body-default-font-size);
  font-weight: var(--leaf-typography-body-default-font-weight);
  line-height: var(--leaf-typography-body-default-line-height);
  -webkit-text-decoration: var(--leaf-typography-body-default-text-decoration);
          text-decoration: var(--leaf-typography-body-default-text-decoration);
  text-transform: var(--leaf-typography-body-default-text-transform);
}
:host table thead {
  margin-top: 0;
}
:host table thead a, :host table thead {
  font-family: var(--leaf-typography-body-default-font-family);
  font-size: var(--leaf-typography-body-default-font-size);
  font-weight: var(--leaf-typography-body-default-font-weight);
  line-height: var(--leaf-typography-body-default-line-height);
  -webkit-text-decoration: var(--leaf-typography-body-default-text-decoration);
          text-decoration: var(--leaf-typography-body-default-text-decoration);
  text-transform: var(--leaf-typography-body-default-text-transform);
  font-weight: var(--leaf-typography-title-large-font-weight);
}
:host table tr:not(:last-child) {
  border-bottom: var(--table-border);
}
:host table th {
  border-right: var(--table-border);
  line-height: var(--leaf-line-heights-5);
  padding: var(--leaf-spacing-16);
  text-align: left;
  vertical-align: middle;
}
:host table td {
  border-right: var(--table-border);
  min-height: var(--table-row-min-height);
  padding: var(--leaf-spacing-16);
  vertical-align: middle;
}
:host table td *:first-of-type:not([class*=mobileHeader]) {
  margin: 0 !important;
}
:host table td ul {
  margin-block-end: 0;
  margin-block-start: 0;
  margin-inline-end: 0;
  margin-inline-start: 0;
  padding-inline-start: 20px;
}
:host table td li:not(:first-child) {
  margin-top: var(--leaf-spacing-16);
}
:host table thead th {
  background-color: var(--table-header-background-color);
  color: var(--table-header-font-color);
}
@media (max-width: 59.999rem) {
  :host table thead th {
    min-width: var(--table-th-min-width);
  }
}
:host table thead tr {
  border-bottom: var(--table-border);
}
:host table a [slot=main-content] {
  word-break: keep-all;
}
:host ::slotted([slot=caption]) {
  color: inherit;
  display: block;
  font-weight: var(--leaf-typography-title-large-font-weight);
  padding-bottom: var(--table-spacing);
  padding-top: var(--table-spacing);
  text-align: left;
}
:host .inner {
  overflow-x: auto;
}
:host .outer {
  border: var(--table-border);
  border-radius: var(--leaf-border-radius-default);
  overflow: hidden;
}
:host .outer th:first-child,
:host .outer td:first-child {
  border-left: transparent;
  word-break: normal;
}
:host .outer th:last-child,
:host .outer td:last-child {
  border-right: transparent;
  word-break: normal;
}
:host .mobileHeader {
  display: none;
}

:host([variant=default]) {
  --chc-table-min-width: 375px;
  display: inline-grid;
}
:host([variant=default]) table {
  min-width: var(--chc-table-min-width);
}

:host([variant=default]) td > *:not(:first-child),
:host([variant=scrollable]) td > *:not(:first-child) {
  display: block;
}

:host([variant=scrollable]) {
  display: grid;
  max-width: 100%;
}
:host([variant=scrollable]) table thead th {
  min-width: auto;
}
:host([variant=scrollable]) table tbody tr th,
:host([variant=scrollable]) table tbody tr td {
  font-family: var(--leaf-typography-body-default-font-family);
  font-size: var(--leaf-typography-body-default-font-size);
  font-weight: var(--leaf-typography-body-default-font-weight);
  line-height: var(--leaf-typography-body-default-line-height);
  -webkit-text-decoration: var(--leaf-typography-body-default-text-decoration);
          text-decoration: var(--leaf-typography-body-default-text-decoration);
  text-transform: var(--leaf-typography-body-default-text-transform);
  font-weight: var(--leaf-typography-body-default-font-weight);
}
:host([variant=scrollable]) .outer {
  margin: auto;
  position: relative;
  width: 100%;
}
:host([variant=scrollable]) .outer th,
:host([variant=scrollable]) .outer td {
  vertical-align: middle;
}
:host([variant=scrollable]) .inner {
  overscroll-behavior: none;
}
:host([variant=scrollable]) .inner.with-margin table thead tr th:first-child,
:host([variant=scrollable]) .inner.with-margin table thead tr td:first-child,
:host([variant=scrollable]) .inner.with-margin table tbody tr th:first-child,
:host([variant=scrollable]) .inner.with-margin table tbody tr td:first-child {
  display: none;
}
:host([variant=scrollable]) .inner.with-margin table colgroup col:first-child {
  display: none;
}
:host([variant=scrollable]) .fixed-column {
  left: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
}
:host([variant=scrollable]) .fixed-column tr {
  border-bottom-color: transparent;
}
:host([variant=scrollable]) .fixed-column th,
:host([variant=scrollable]) .fixed-column td {
  visibility: hidden;
}
:host([variant=scrollable]) .fixed-column td,
:host([variant=scrollable]) .fixed-column th {
  border-color: transparent;
}
:host([variant=scrollable]) .fixed-column th:first-child,
:host([variant=scrollable]) .fixed-column td:first-child {
  border: var(--table-border);
  border-left: transparent;
  border-top: 0;
  visibility: visible;
}
@media (max-width: 34.999rem) {
  :host([variant=scrollable]) .fixed-column thead th:first-child {
    box-shadow: var(--table-fixed-column-elevation);
  }
}
:host([variant=scrollable]) .fixed-column tbody th:first-child,
:host([variant=scrollable]) .fixed-column tbody td:first-child,
:host([variant=scrollable]) .fixed-column tfoot th:first-child,
:host([variant=scrollable]) .fixed-column tfoot td:first-child {
  background: var(--leaf-color-bg-default);
  border: var(--table-border);
  border-left: transparent;
  pointer-events: initial;
  visibility: visible;
}
@media (max-width: 34.999rem) {
  :host([variant=scrollable]) .fixed-column tbody th:first-child,
  :host([variant=scrollable]) .fixed-column tbody td:first-child,
  :host([variant=scrollable]) .fixed-column tfoot th:first-child,
  :host([variant=scrollable]) .fixed-column tfoot td:first-child {
    box-shadow: var(--table-fixed-column-elevation);
  }
}
:host([variant=scrollable]) .fixed-column tbody tr:last-child th:first-child,
:host([variant=scrollable]) .fixed-column tbody tr:last-child td:first-child {
  border-bottom: 0px;
}
:host([variant=scrollable]) .fixed-column tbody tr th:first-child {
  border-top: 0px;
}
@media (max-width: 34.999rem) {
  :host([variant=scrollable]) table {
    table-layout: auto;
  }
}

:host([variant=scrollable][table-header-color=green]) thead th a {
  font-family: var(--leaf-typography-body-default-link-font-family);
  font-size: inherit;
  font-weight: var(--leaf-typography-body-default-link-font-weight);
  line-height: inherit;
  -webkit-text-decoration: var(--leaf-text-decoration-none);
          text-decoration: var(--leaf-text-decoration-none);
  text-transform: var(--leaf-typography-body-default-link-text-transform);
  border-bottom: var(--leaf-border-width-default) solid;
  color: var(--leaf-color-content-link);
  word-break: break-word;
  color: var(--leaf-color-content-knockout);
}
:host([variant=scrollable][table-header-color=green]) thead th a:hover {
  color: var(--leaf-color-content-link-hover);
}
:host([variant=scrollable][table-header-color=green]) thead th a:focus {
  background-color: var(--leaf-color-bg-active);
  border-color: var(--leaf-color-border-transparent);
  color: var(--leaf-color-content-knockout);
  outline: none;
}
:host([variant=scrollable][table-header-color=green]) thead th a:hover {
  color: var(--leaf-color-content-brand-knockout);
}
:host([variant=scrollable][table-header-color=green]) thead th a:focus {
  background-color: var(--leaf-color-content-knockout);
  color: var(--leaf-color-bg-active);
}
:host([variant=scrollable][table-header-color=green]) thead th a:where(.leaf-u-link--external)::after {
  background-color: currentColor;
  content: ".";
  margin: 0 0 0 var(--leaf-spacing-4);
  padding: 0 calc(var(--leaf-spacing-8) - var(--leaf-spacing-2));
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' id='svg_1' fill='%23333333' d='m28.667,28.667l-9.334,0l0,-9.334l4.667,0l0,-1.333l-4.667,0c-0.74,0 -1.333,0.6 -1.333,1.333l0,9.334c0,0.733 0.593,1.333 1.333,1.333l9.334,0c0.733,0 1.333,-0.6 1.333,-1.333l0,-4.667l-1.333,0l0,4.667zm-3.334,-10.667l0,1.333l2.394,0l-6.554,6.554l0.94,0.94l6.554,-6.554l0,2.394l1.333,0l0,-4.667l-4.667,0z'/%3E%3C/g%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' id='svg_1' fill='%23333333' d='m28.667,28.667l-9.334,0l0,-9.334l4.667,0l0,-1.333l-4.667,0c-0.74,0 -1.333,0.6 -1.333,1.333l0,9.334c0,0.733 0.593,1.333 1.333,1.333l9.334,0c0.733,0 1.333,-0.6 1.333,-1.333l0,-4.667l-1.333,0l0,4.667zm-3.334,-10.667l0,1.333l2.394,0l-6.554,6.554l0.94,0.94l6.554,-6.554l0,2.394l1.333,0l0,-4.667l-4.667,0z'/%3E%3C/g%3E%3C/svg%3E");
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
}
:host([variant=scrollable][table-header-color=green]) thead th a:where(.leaf-u-link--lock)::after {
  background-color: currentColor;
  content: ".";
  margin: 0 0 0 var(--leaf-spacing-8);
  padding: 0 calc(var(--leaf-spacing-8) - var(--leaf-spacing-2));
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' d='m28.5,21.25l-0.75,0l0,-1.571c0,-2.169 -1.68,-3.929 -3.75,-3.929c-2.07,0 -3.75,1.76 -3.75,3.929l0,1.571l-0.75,0c-0.825,0 -1.5,0.707 -1.5,1.571l0,7.858c0,0.864 0.675,1.571 1.5,1.571l9,0c0.825,0 1.5,-0.707 1.5,-1.571l0,-7.859c0,-0.864 -0.675,-1.571 -1.5,-1.571l0,0.001zm-6.75,-1.571c0,-1.305 1.005,-2.358 2.25,-2.358s2.25,1.053 2.25,2.358l0,1.571l-4.5,0l0,-1.571zm6.75,11l-9,0l0,-7.859l9,0l0,7.858l0,0.001zm-4.5,-2.359c0.825,0 1.5,-0.707 1.5,-1.571s-0.675,-1.571 -1.5,-1.571s-1.5,0.707 -1.5,1.571s0.675,1.571 1.5,1.571z' fill='%23333333' id='svg_1'/%3E%3C/g%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' fill='none'%3E%3Cg%3E%3Ctitle%3ELayer 1%3C/title%3E%3Cpath xmlns='http://www.w3.org/2000/svg' d='m28.5,21.25l-0.75,0l0,-1.571c0,-2.169 -1.68,-3.929 -3.75,-3.929c-2.07,0 -3.75,1.76 -3.75,3.929l0,1.571l-0.75,0c-0.825,0 -1.5,0.707 -1.5,1.571l0,7.858c0,0.864 0.675,1.571 1.5,1.571l9,0c0.825,0 1.5,-0.707 1.5,-1.571l0,-7.859c0,-0.864 -0.675,-1.571 -1.5,-1.571l0,0.001zm-6.75,-1.571c0,-1.305 1.005,-2.358 2.25,-2.358s2.25,1.053 2.25,2.358l0,1.571l-4.5,0l0,-1.571zm6.75,11l-9,0l0,-7.859l9,0l0,7.858l0,0.001zm-4.5,-2.359c0.825,0 1.5,-0.707 1.5,-1.571s-0.675,-1.571 -1.5,-1.571s-1.5,0.707 -1.5,1.571s0.675,1.571 1.5,1.571z' fill='%23333333' id='svg_1'/%3E%3C/g%3E%3C/svg%3E");
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: var(--leaf-spacing-40);
          mask-size: var(--leaf-spacing-40);
}
:host([variant=scrollable][table-header-color=green]) thead th a:where(.leaf-u-link--inverted) {
  color: var(--leaf-color-content-knockout);
}
:host([variant=scrollable][table-header-color=green]) thead th a:where(.leaf-u-link--inverted):hover {
  color: var(--leaf-color-content-brand-knockout);
}
:host([variant=scrollable][table-header-color=green]) thead th a:where(.leaf-u-link--inverted):focus {
  background-color: var(--leaf-color-content-knockout);
  color: var(--leaf-color-bg-active);
}
:host([variant=scrollable][table-header-color=green]) thead th a:where(.leaf-u-link--action) {
  border-bottom: none;
  display: inline-block;
  line-height: var(--leaf-spacing-32);
  margin: var(--leaf-spacing-8) calc(var(--leaf-spacing-48) - var(--leaf-spacing-4));
  margin-left: 0;
  position: relative;
}
:host([variant=scrollable][table-header-color=green]) thead th a:where(.leaf-u-link--action)::before {
  content: "";
  display: inline-block;
  height: 100%;
  min-height: var(--leaf-spacing-40);
  vertical-align: middle;
}
:host([variant=scrollable][table-header-color=green]) thead th a:where(.leaf-u-link--action)::after {
  background-color: currentColor;
  bottom: 0;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='white'/%3E%3C/svg%3E");
  height: var(--leaf-spacing-40);
  margin-left: var(--leaf-spacing-4);
  position: absolute;
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23001F99'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='white'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23001F99' stroke-width='2'/%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23001F99'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='white'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23001F99' stroke-width='2'/%3E%3C/svg%3E");
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  width: var(--leaf-spacing-40);
}
:host([variant=scrollable][table-header-color=green]) thead th a:where(.leaf-u-link--action):focus:after {
  border-radius: 50%;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5.28027' y='5' width='30' height='30' rx='15' fill='%23001F99'/%3E%3Cpath d='M20.2803 12L18.8703 13.41L24.4503 19H12.2803V21H24.4503L18.8703 26.59L20.2803 28L28.2803 20L20.2803 12Z' fill='white'/%3E%3Crect x='39.2803' y='39' width='38' height='38' rx='19' transform='rotate(180 39.2803 39)' stroke='black' stroke-width='2'/%3E%3Crect x='5.28027' y='5' width='30' height='30' rx='15' stroke='%23001F99' stroke-width='2'/%3E%3C/svg%3E");
  -webkit-mask: none;
          mask: none;
}
:host([variant=scrollable][table-header-color=green]) thead th a:where(.leaf-u-link--action):where(.leaf-u-link--inverted)::after {
  border-radius: 50%;
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3C/svg%3E");
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='white'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='white' stroke-width='2'/%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='white'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='white' stroke-width='2'/%3E%3C/svg%3E");
}
:host([variant=scrollable][table-header-color=green]) thead th a:where(.leaf-u-link--action):where(.leaf-u-link--inverted):hover::after {
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23E6EBFF'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23E6EBFF' stroke-width='2'/%3E%3C/svg%3E");
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23E6EBFF'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='transparent' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23E6EBFF' stroke-width='2'/%3E%3C/svg%3E");
}
:host([variant=scrollable][table-header-color=green]) thead th a:where(.leaf-u-link--action):where(.leaf-u-link--inverted):focus:after {
  content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 41 40' fill='none'%3E%3Crect x='5' y='5' width='30' height='30' rx='15' fill='%23E6EBFF'/%3E%3Cpath d='M20 12L18.59 13.41L24.17 19H12V21H24.17L18.59 26.59L20 28L28 20L20 12Z' fill='%230033FF'/%3E%3Crect x='39' y='39' width='38' height='38' rx='19' transform='rotate(180 39 39)' stroke='white' stroke-width='2'/%3E%3Crect x='5' y='5' width='30' height='30' rx='15' stroke='%23E6EBFF' stroke-width='2'/%3E%3C/svg%3E");
  -webkit-mask: none;
          mask: none;
}

:host([variant=graphical]) {
  --icon-color: var(--leaf-color-content-subtle);
  --icon-size: calc(var(--leaf-spacing-24) + var(--leaf-spacing-4));
  --icon-size-desktop: calc(var(--leaf-spacing-48) + var(--leaf-spacing-4));
}
:host([variant=graphical]) th,
:host([variant=graphical]) td {
  text-align: center;
  vertical-align: middle;
}
:host([variant=graphical]) th div:not([class*=mobileHeader]),
:host([variant=graphical]) td div:not([class*=mobileHeader]) {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 132px;
  min-width: 260px;
}
:host([variant=graphical]) leaf-icon {
  --icon-height: var(--icon-size);
  --icon-width: var(--icon-size);
  --icon-fill: var(--icon-color);
  flex-shrink: 0;
  margin-right: var(--leaf-spacing-8);
}
@media (min-width: 35rem) {
  :host([variant=graphical]) leaf-icon {
    --icon-height: var(--icon-size-desktop);
    --icon-width: var(--icon-size-desktop);
  }
}
@media (max-width: 34.999rem) {
  :host([variant=graphical]) table tbody tr td:first-child {
    border-right-color: var(--table-border-color);
    padding: var(--leaf-spacing-32) var(--leaf-spacing-16);
  }
  :host([variant=graphical]) table tbody tr td div:not([class*=mobileHeader]) {
    display: block;
    min-height: 0;
    min-width: 0;
  }
}

:host([variant=column-stacker]) table thead,
:host([variant=row-stacker]) table thead {
  display: table-header-group;
}

:host([variant=striped]) tr:nth-child(even) {
  background-color: var(--leaf-color-bg-subtle);
}

:host([table-bold-headings]) table tbody tr th {
  font-weight: var(--leaf-typography-title-large-font-weight);
}

@media (max-width: 34.999rem) {
  :host([variant=column-stacker]),
  :host([variant=row-stacker]),
  :host([variant=graphical]) {
    --table-dark-border-color: transparent;
  }
  :host([variant=column-stacker]) table,
  :host([variant=row-stacker]) table,
  :host([variant=graphical]) table {
    word-wrap: break-word;
  }
  :host([variant=column-stacker]) table thead,
  :host([variant=row-stacker]) table thead,
  :host([variant=graphical]) table thead {
    display: none;
  }
  :host([variant=column-stacker]) table tbody,
  :host([variant=row-stacker]) table tbody,
  :host([variant=graphical]) table tbody {
    display: block;
  }
  :host([variant=column-stacker]) table tbody tr,
  :host([variant=row-stacker]) table tbody tr,
  :host([variant=graphical]) table tbody tr {
    display: block;
  }
  :host([variant=column-stacker]) table tbody tr td,
  :host([variant=row-stacker]) table tbody tr td,
  :host([variant=graphical]) table tbody tr td {
    display: block;
    padding: var(--leaf-spacing-8) var(--leaf-spacing-16);
    width: 100%;
  }
  :host([variant=column-stacker]) .outer,
  :host([variant=row-stacker]) .outer,
  :host([variant=graphical]) .outer {
    border: 0;
  }
  :host([variant=column-stacker]) .mobileHeader,
  :host([variant=row-stacker]) .mobileHeader,
  :host([variant=graphical]) .mobileHeader {
    font-family: var(--leaf-typography-body-default-font-family);
    font-size: var(--leaf-typography-body-default-font-size);
    font-weight: var(--leaf-typography-body-default-font-weight);
    line-height: var(--leaf-typography-body-default-line-height);
    -webkit-text-decoration: var(--leaf-typography-body-default-text-decoration);
            text-decoration: var(--leaf-typography-body-default-text-decoration);
    text-transform: var(--leaf-typography-body-default-text-transform);
    display: block;
    font-weight: var(--leafdcws-font-weight-semibold);
    margin-bottom: var(--leaf-spacing-8);
  }
}
@media (max-width: 34.999rem) {
  :host([variant=row-stacker]) table tbody tr,
  :host([variant=graphical]) table tbody tr {
    border: var(--table-border);
    border-radius: var(--leaf-border-radius-default);
    margin-bottom: var(--leaf-spacing-24);
  }
  :host([variant=row-stacker]) table tbody tr td,
  :host([variant=graphical]) table tbody tr td {
    border: 0;
    border-bottom: var(--table-border);
  }
  :host([variant=row-stacker]) table tbody tr td:first-child,
  :host([variant=graphical]) table tbody tr td:first-child {
    border-top-left-radius: var(--leaf-border-radius-default);
    border-top-right-radius: var(--leaf-border-radius-default);
  }
  :host([variant=row-stacker]) table tbody tr td:last-child,
  :host([variant=graphical]) table tbody tr td:last-child {
    border-bottom: 0;
    border-bottom-left-radius: var(--leaf-border-radius-default);
    border-bottom-right-radius: var(--leaf-border-radius-default);
  }
}

@media (max-width: 34.999rem) {
  :host([variant=column-stacker]) {
    --table-dark-border-color: var(--leaf-color-border-default);
  }
  :host([variant=column-stacker]) table tbody td {
    border: var(--table-border) !important;
    border-radius: var(--leaf-border-radius-default);
    margin-bottom: var(--leaf-spacing-24);
  }
}

:host([table-header-color=orange]) {
  --table-header-background-color: var(--leafdcws-accent-1-subtle);
}

:host([table-header-color=green]) {
  --table-header-background-color: var(--leafdcws-accent-2-xstrong);
  --table-header-font-color: var(--leaf-color-content-knockout);
}`;
const slotStyles = "leaf-accordion-panel chc-table[variant=default] {\n  display: grid;\n}";
var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};
const _CHCTable = class _CHCTable extends LeafElement {
  constructor() {
    super();
    this.tableInitialLoad = false;
    this.addDarkBorder = false;
    this.addBoldHeadings = false;
    this.variant = "default";
    this.headerColor = "default";
    this.stackerVariants = ["column-stacker", "row-stacker", "graphical"];
    appendStylesheet("chc-table-styles", slotStyles);
  }
  get slottedTable() {
    return this.querySelector('[slot="table"]');
  }
  renderColumnWidths() {
    var _a;
    return (_a = this.colwidths) == null ? void 0 : _a.map(
      (width) => width !== "" ? `<col style="min-width: ${width};width: ${width};max-width: 100%;" />` : `<col style="min-width: 100%;" />`
    ).join("");
  }
  renderCustomColWidths() {
    var _a;
    const tableHeads = (_a = this.shadowRoot) == null ? void 0 : _a.querySelectorAll("table thead");
    tableHeads == null ? void 0 : tableHeads.forEach(
      (thead) => thead.insertAdjacentHTML(
        "beforebegin",
        `<colgroup>${this.renderColumnWidths()}</colgroup>${this.stackerVariants.includes(this.variant) ? `<style>@media (max-width: 575.98px) {colgroup {display: none;}}</style>` : ""}`
      )
    );
  }
  renderCustomMobileHeaders() {
    var _a;
    const body = (_a = this.shadowRoot) == null ? void 0 : _a.querySelectorAll("table tbody tr td");
    if (body) {
      Array.from(body).forEach((cell) => {
        var _a2, _b;
        const matchingHeader = (_a2 = cell.getAttribute("headers")) == null ? void 0 : _a2.split(" ")[0];
        const header = (_b = this.shadowRoot) == null ? void 0 : _b.querySelector(`#${matchingHeader}`);
        const headerText = header == null ? void 0 : header.innerHTML.trim().replace(/(<\/div>)|(<div>)/gi, "");
        if (headerText && headerText !== "&nbsp;") {
          cell.insertAdjacentHTML("afterbegin", `<div class="mobileHeader">${headerText}</div>`);
        }
      });
    }
  }
  render() {
    return html`<div class="outer"><div class="inner"></div></div>`;
  }
  setScrollable() {
    var _a, _b, _c;
    const tableElement = (_a = this.shadowRoot) == null ? void 0 : _a.querySelector("table");
    const outerElement = (_b = this.shadowRoot) == null ? void 0 : _b.querySelector(".outer");
    const innerElement = (_c = this.shadowRoot) == null ? void 0 : _c.querySelector(".inner");
    if (tableElement && outerElement && innerElement && outerElement.querySelectorAll(".fixed-column").length === 0) {
      const cloneElement = tableElement.cloneNode(true);
      cloneElement.classList.add("fixed-column");
      cloneElement.setAttribute("aria-hidden", "true");
      cloneElement.innerHTML = cloneElement.innerHTML.replace(/table(\d+)-/g, (_match, n) => `table${n}-cc-`);
      outerElement.appendChild(cloneElement);
    }
  }
  async startTableRender() {
    if (this.variant === "scrollable")
      this.setScrollable();
    if (this.colwidths)
      this.renderCustomColWidths();
    if (this.stackerVariants.includes(this.variant))
      this.renderCustomMobileHeaders();
  }
  async updated(changedProperties) {
    super.updated(changedProperties);
    await this.updateComplete;
    const observer = new IntersectionObserver(
      async (entries) => {
        var _a;
        if (entries[0].isIntersecting && this.slottedTable && !this.tableInitialLoad) {
          const slottedTable = this.slottedTable.cloneNode(true);
          const tableRoot = this.shadowRoot;
          tableRoot == null ? void 0 : tableRoot.querySelectorAll("table").forEach((table) => table.remove());
          (_a = tableRoot == null ? void 0 : tableRoot.querySelector(".outer .inner")) == null ? void 0 : _a.insertAdjacentElement("beforeend", slottedTable);
          this.tableInitialLoad = true;
          this.startTableRender();
        }
      },
      { threshold: [0] }
    );
    observer.observe(this.renderRoot.host);
  }
  async firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
    await this.updateComplete;
    this.startTableRender();
  }
};
_CHCTable.styles = unsafeCSS(styles);
let CHCTable = _CHCTable;
__decorateClass([
  state()
], CHCTable.prototype, "tableInitialLoad", 2);
__decorateClass([
  property({ reflect: true, type: Array })
], CHCTable.prototype, "colwidths", 2);
__decorateClass([
  property({ reflect: true, type: Boolean, attribute: "table-dark-border" })
], CHCTable.prototype, "addDarkBorder", 2);
__decorateClass([
  property({ reflect: true, type: Boolean, attribute: "table-bold-headings" })
], CHCTable.prototype, "addBoldHeadings", 2);
__decorateClass([
  property({ reflect: true })
], CHCTable.prototype, "variant", 2);
__decorateClass([
  property({ reflect: true, attribute: "table-header-color" })
], CHCTable.prototype, "headerColor", 2);
__decorateClass([
  state()
], CHCTable.prototype, "stackerVariants", 2);
export {
  CHCTable
};
//# sourceMappingURL=table.component.js.map
